import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'

import { jobOpportunitiesApi } from '../../services/api'
import type { JobOpportunity } from '../../state/jobOpportunities'
import {
  jobOpportunitiesAtom,
  SIGNAL_ACTION_LABELS,
  WATCH_TYPES,
  resolveWatchType,
} from '../../state/jobOpportunities'
import type { SignalAction, WatchType } from '../../state/jobOpportunities'
import { useClientNetwork } from '../../hooks/useClientNetwork'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket } from '../../hooks/useWebSocket'
import { isSignalJob } from '../job-applications/jobTabRouting'
import JobDetailDialog from '../job-applications/JobDetailDialog'
import LogHiddenOpportunityDialog from '../job-applications/LogHiddenOpportunityDialog'
import SignalsTable from './SignalsTable'

interface SignalsPageProps {
  clientId: string
  clientName: string
  // Staff and the client read and write the same table. The audience changes
  // the wording and whether the page can log a new signal: only staff can.
  audience: 'staff' | 'client'
  // Staff only. Set when logging is unavailable and says why, e.g. the client
  // is no longer on the market; the button is then disabled with the reason.
  logDisabledReason?: string
  // Rendered beside the title: the staff page puts its link back to Front Door
  // here.
  headerExtra?: React.ReactNode
}

/**
 * MWA-421 — the Signals page: a client's hidden opportunities as two tables,
 * Signal Watch and Seat Watch, with an Action on every row.
 *
 * One page for both audiences. It reads the client's jobs, keeps the shared
 * jobs atom in step over the WebSocket the way the Front Door pages do, and
 * writes the Action through update-job-opportunity, which is where a client's
 * right to write only that field is enforced.
 */
export default function SignalsPage({
  clientId,
  clientName,
  audience,
  logDisabledReason,
  headerExtra,
}: SignalsPageProps) {
  const [jobs, setJobs] = useAtom(jobOpportunitiesAtom)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingActionId, setSavingActionId] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState<JobOpportunity | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const { showSuccess, showError } = useNotification()
  const network = useClientNetwork(clientId)

  const [searchParams, setSearchParams] = useSearchParams()
  const watchParam = searchParams.get('watch')
  const watchType: WatchType = (WATCH_TYPES as readonly string[]).includes(watchParam ?? '')
    ? (watchParam as WatchType)
    : 'signal'
  const setWatchType = useCallback(
    (next: WatchType) => {
      const params = new URLSearchParams(searchParams)
      if (next === 'signal') params.delete('watch')
      else params.set('watch', next)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Replaces this client's rows only. The atom is shared with the Front Door
  // pages and the staff board holds every client's, so a read for one client
  // must not wipe the rest.
  const loadJobs = useCallback(async () => {
    setLoading(true)
    try {
      const result = await jobOpportunitiesApi.list({ clientId })
      const fresh = Array.isArray(result) ? result : []
      setJobs((prev) => [...prev.filter((j) => j.clientId !== clientId), ...fresh])
      setError(null)
    } catch (err) {
      console.error('Failed to load signals:', err)
      setError(`list-job-opportunities: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setLoading(false)
    }
  }, [clientId, setJobs])

  useEffect(() => {
    loadJobs()
  }, [loadJobs])

  const wsHandlers = useMemo(
    () => ({
      job_opportunity_created: (message: { data: unknown }) => {
        const created = message.data as JobOpportunity
        if (created.clientId !== clientId) return
        setJobs((prev) => (prev.some((j) => j.id === created.id) ? prev : [...prev, created]))
      },
      job_opportunity_updated: (message: { data: unknown }) => {
        const updated = message.data as JobOpportunity
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
      },
      job_opportunity_deleted: (message: { data: unknown }) => {
        const data = message.data as { id: string }
        setJobs((prev) => prev.filter((j) => j.id !== data.id))
      },
      ws_reconnected: () => {
        loadJobs()
      },
    }),
    [clientId, setJobs, loadJobs]
  )
  useWebSocket(wsHandlers)

  const signals = useMemo(
    () => jobs.filter((j) => j.clientId === clientId && isSignalJob(j)),
    [jobs, clientId]
  )

  // Not optimistic: the select is a controlled value read from the job, so a
  // refused write leaves it showing what the record really holds rather than
  // snapping back after the fact.
  const handleActionChange = async (job: JobOpportunity, action: SignalAction) => {
    setSavingActionId(job.id)
    try {
      const result = await jobOpportunitiesApi.update({ id: job.id, signalAction: action })
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...result, hasDuplicate: undefined } : j)))
      showSuccess(`${job.company}: ${SIGNAL_ACTION_LABELS[action]}`)
    } catch (err) {
      console.error('Failed to save the action:', err)
      showError(
        audience === 'client'
          ? 'We could not save that. Please try again.'
          : err instanceof Error
            ? err.message
            : 'Failed to save the action'
      )
    } finally {
      setSavingActionId(null)
    }
  }

  const handleLog = async (data: Parameters<typeof jobOpportunitiesApi.create>[0]) => {
    try {
      const created = await jobOpportunitiesApi.create(data)
      setJobs((prev) => (prev.some((j) => j.id === created.id) ? prev : [...prev, created]))
      // Show the table it landed in, so a logged signal never seems to vanish.
      setWatchType(resolveWatchType(created))
      showSuccess('Signal logged')
    } catch (err) {
      console.error('Failed to log the signal:', err)
      showError(err instanceof Error ? err.message : 'Failed to log the signal')
      throw err
    }
  }

  const handleOpen = (job: JobOpportunity) => {
    setSelectedJob(job)
    setDetailOpen(true)
  }

  const handleJobUpdate = (updated: JobOpportunity) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
    setSelectedJob(updated)
  }

  const handleJobDelete = (id: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== id))
  }

  return (
    <Box sx={{ height: { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 120px)' }, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Signals
            </Typography>
            {audience === 'staff' && clientName && (
              <Typography variant="body2" color="text.secondary">
                {clientName}
              </Typography>
            )}
          </Box>
          {headerExtra}
        </Stack>
        {audience === 'staff' && (
          <Tooltip title={logDisabledReason ?? ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!!logDisabledReason}
                onClick={() => setLogOpen(true)}
              >
                Log a Signal
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <SignalsTable
          jobs={signals}
          audience={audience}
          loading={loading}
          error={error}
          onRetry={loadJobs}
          onOpen={handleOpen}
          onActionChange={handleActionChange}
          savingActionId={savingActionId}
          watchType={watchType}
          onWatchTypeChange={setWatchType}
        />
      </Box>

      {audience === 'staff' && (
        <LogHiddenOpportunityDialog
          open={logOpen}
          onClose={() => setLogOpen(false)}
          clientId={clientId}
          clientName={clientName}
          onLog={handleLog}
        />
      )}

      <JobDetailDialog
        contacts={network.contacts}
        interactions={network.interactions}
        contactsLoading={network.loading}
        contactsError={network.error}
        onRetryContacts={network.retry}
        onContactUpdate={audience === 'staff' ? network.replaceContact : undefined}
        open={detailOpen}
        job={selectedJob}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleJobUpdate}
        onDelete={handleJobDelete}
      />
    </Box>
  )
}
